/* eslint-disable @typescript-eslint/no-explicit-any */
import * as moment from 'moment';
import { DateUtils } from './date.utils';
import { TagUtils } from './tag.utils';

/**
 * Mapping from URL query param keys to API request param names.
 * Keys that map to themselves (e.g. 'event_ids') are not listed here.
 */
const PARAM_KEY_MAP: Record<string, string> = {
  feed: 'feed_types',
  categories: 'exclude_document_category_filter',
  continents: 'source_continents',
  countries: 'source_countries',
  ranks: 'source_ranks',
  panels: 'source_panels',
  participant: 'with_contained_events_participant',
};

/**
 * Single source of truth for the URL short-label → factuality wire-value
 * expansion. Consumed by:
 *   - `ParamsBuilder.build` (joins the values into a v2 query-string)
 *   - `CommonDataParamsService.getFactualityValues` (returns the flat array
 *     for v2 score_ts / v4 news / v5 events POST bodies)
 *
 * Iteration order matters — it determines the order of the emitted values
 * (matched on substring `factualityParam.includes(key)`).
 */
export const FACTUALITY_VALUE_MAP: Record<string, string[]> = {
  na: ['NONE', 'UNKNOWN'],
  fact: ['FACT', 'COUNTERFACT'],
  probable: ['PROBABLE', 'COUNTERPROBABLE'],
  possible: ['POSSIBLE', 'COUNTERPOSSIBLE'],
};

export interface ParamsBuilderOptions {
  /** Whether the current context is a reader/story-reader (defaults time to 90 days) */
  isReader?: boolean;
}

/**
 * Pure utility that builds API request params from URL query params.
 * Replaces the `myParams()` method from `yukkapi.service.ts`.
 *
 * Usage:
 *   const params = ParamsBuilder.build(iparams, ['period', ...ParamsBuilder.PARAMS_FOR_ALL_REQUESTS], project);
 */
export class ParamsBuilder {
  static readonly PARAMS_FOR_ALL_REQUESTS = [
    'from',
    'to',
    'lang',
    'feed',
    'categories',
    'continents',
    'countries',
    'factuality',
    'temporality',
    'ranks',
    'panels',
  ] as const;

  static build(
    iparams: Record<string, any>,
    paramsToBeSent: readonly string[],
    project: string,
    options?: ParamsBuilderOptions,
  ): Record<string, any> {
    const params: Record<string, any> = {};
    params['project_id'] = project;

    // Language
    if (
      paramsToBeSent.includes('lang') &&
      iparams.lang &&
      iparams.lang !== 'multi'
    ) {
      params['lang'] = iparams.lang;
    }

    // Date range — `newsday` narrows the range to a single day and overrides
    // `time`; otherwise use the URL `time` (or 90d in reader contexts).
    let fromValue: string;
    let toValue: string;
    if (iparams.newsday) {
      fromValue = moment(iparams.newsday).format('YYYY-MM-DD');
      toValue = moment(iparams.newsday).add(1, 'days').format('YYYY-MM-DD');
    } else {
      const time = iparams.time || (options?.isReader ? '90' : '7');
      const fromto = DateUtils.fromTo(time);
      fromValue = fromto.dateFrom;
      toValue = fromto.dateTo;
    }
    if (paramsToBeSent.includes('from')) {
      params['from'] = fromValue;
    }
    if (paramsToBeSent.includes('to')) {
      params['to'] = toValue;
    }

    // Period
    if (paramsToBeSent.includes('period')) {
      params['period'] = iparams.period || 7;
    }

    // Simple mapped params (feed → feed_types, categories → exclude_document_category_filter, etc.)
    for (const [key, apiKey] of Object.entries(PARAM_KEY_MAP)) {
      if (paramsToBeSent.includes(key) && iparams[key]) {
        params[apiKey] = iparams[key];
      }
    }

    // Direct pass-through params
    if (paramsToBeSent.includes('event_ids') && iparams.event_ids) {
      params['event_ids'] = iparams.event_ids;
    }

    // Tag (only valid if it has 3 colon-separated parts). The `tag` query param
    // may carry a `|`-suffixed display label; strip it before forwarding.
    if (paramsToBeSent.includes('tag') && TagUtils.isValid(iparams.tag)) {
      params['tag'] = iparams.tag.split('|')[0];
    }

    // Top events
    if (paramsToBeSent.includes('with_top_events') && iparams.with_top_events) {
      params['with_top_events'] = iparams.with_top_events;
    }
    if (paramsToBeSent.includes('with_all_events') && iparams.with_all_events) {
      params['n_events'] = -1;
    }

    // Temporality (special: 'na' → 'NONE,UNKNOWN')
    if (paramsToBeSent.includes('temporality') && iparams.temporality) {
      params['temporality'] =
        iparams.temporality === 'na' ? 'NONE,UNKNOWN' : iparams.temporality;
    }

    // Factuality (compound: multiple selections joined)
    if (paramsToBeSent.includes('factuality') && iparams.factuality) {
      const factuality: string[] = [];
      for (const [key, expanded] of Object.entries(FACTUALITY_VALUE_MAP)) {
        if (iparams.factuality.includes(key)) {
          factuality.push(...expanded);
        }
      }
      if (factuality.length > 0) {
        params['factuality'] = factuality.join(',');
      }
    }

    return params;
  }
}
